import Fuse from 'fuse.js';
import { useCallback, useMemo } from 'react';

interface UseFuseSearchCallbackOptions<T> {
	items: T[];
	keys: Fuse.FuseOptionKey<T>[];
	options?: Fuse.IFuseOptions<T>;
	filterFn?: (item: T) => boolean;
}

export function useFuseSearchCallback<T>({
	items,
	keys,
	options = { threshold: 0.6, includeScore: true, ignoreLocation: true },
	filterFn,
}: UseFuseSearchCallbackOptions<T>): (searchTerm: string) => T[] {
	const fuse = useMemo(
		() => new Fuse(items, { ...options, keys }),
		[items, options, keys]
	);

	return useCallback(
		(searchTerm: string) => {
			if (!searchTerm && !filterFn) {
				return items;
			}

			const filteredItems = filterFn ? items.filter(filterFn) : items;

			if (!searchTerm) {
				return filteredItems;
			}

			return fuse.search(searchTerm).map((result) => result.item);
		},
		[items, fuse, filterFn]
	);
}
